import type { SvgIconComponent } from "@mui/icons-material";
import AcUnitRoundedIcon from "@mui/icons-material/AcUnitRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import { MONEY_EPSILON } from "features/groups/lib/groupBalancesDisplay";
import { participantDisplayName } from "features/groups/lib/groupPeople";
import type { GroupSummary } from "types/Group";
import type { UserEntry } from "types/Group";
import type { GroupTransaction } from "@/types/GroupTransaction";
import { toTimestamp } from "utils/dateUtils";

export type HomeGroupStatusTone = "positive" | "negative" | "settled";

export interface HomeGroupCardModel {
    balance: number;
    balanceLabel: string;
    memberCount: number;
    memberPreview: string;
    lastActivity: string;
    statusTone: HomeGroupStatusTone;
    statusLabel: string;
}

export interface HomeGroupIconModel {
    Icon?: SvgIconComponent;
    letter?: string;
    color: string;
    background: string;
}

const ICON_VARIANTS = [
    { color: "#C77DFF", background: "rgba(157, 78, 221, 0.22)" },
    { color: "#4DA3FF", background: "rgba(77, 163, 255, 0.18)" },
    { color: "#8AF27A", background: "rgba(87, 204, 87, 0.20)" },
    { color: "#F4A261", background: "rgba(244, 162, 97, 0.18)" },
] as const;

export function memberPreviewFor(people: UserEntry[]): string {
    if (people.length === 0) return "";

    const names = people.map(participantDisplayName);
    const visible = names.slice(0, 3);
    const remaining = names.length - visible.length;
    return remaining > 0 ? `${visible.join(" · ")} · +${remaining}` : visible.join(" · ");
}

export function normalizeGroupName(name: string): string {
    return name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
}

export function iconForGroup(groupName: string, index: number): HomeGroupIconModel {
    const normalized = normalizeGroupName(groupName);
    const variant = ICON_VARIANTS[index % ICON_VARIANTS.length];

    if (normalized.includes("ski")) return { Icon: AcUnitRoundedIcon, color: "#4DA3FF", background: "rgba(77, 163, 255, 0.18)" };
    if (normalized.includes("carrito") || normalized.includes("cart")) return { Icon: ShoppingCartRoundedIcon, color: "#F4A261", background: "rgba(244, 162, 97, 0.18)" };
    if (normalized.includes("globant")) return { letter: "G", color: "#8AF27A", background: "rgba(87, 204, 87, 0.20)" };

    return { Icon: GroupsRoundedIcon, ...variant };
}

export function lastActivityLabel(transactions: GroupTransaction[], locale: string, fallback: string): string {
    let latest = 0;
    for (const tx of transactions) {
        latest = Math.max(latest, toTimestamp(tx.date));
    }

    if (!latest) return fallback;
    return new Date(latest).toLocaleDateString(locale, { day: "numeric", month: "short" });
}

export function routeSegmentFor(group: GroupSummary): string {
    return group._id || group.name.replace(/\s/g, "");
}

export function getHomeGroupStatusTone(balance: number): HomeGroupStatusTone {
    if (balance > MONEY_EPSILON) return "positive";
    if (balance < -MONEY_EPSILON) return "negative";
    return "settled";
}
